import React, { useEffect, useState, useCallback } from "react";
import api from "../../lib/api";
import AdminLayout from "../../components/AdminLayout";
import { toast } from "sonner";
import { Database, Trash2, RefreshCw, Search, Eye, Loader2 } from "lucide-react";

const Stat = ({ label, value, accent }) => (
  <div className="io-card-el p-5">
    <div className="kicker">{label}</div>
    <div className={`mt-1.5 font-serif text-[28px] md:text-[32px] ${accent || "text-[var(--io-text)]"}`}>{value}</div>
  </div>
);

const fmtTtl = (ttl) => {
  if (ttl === undefined || ttl === null) return "—";
  if (ttl === -1) return "no expiry";
  if (ttl === -2) return "expired";
  if (ttl < 60) return `${ttl}s`;
  if (ttl < 3600) return `${Math.floor(ttl / 60)}m ${ttl % 60}s`;
  return `${Math.floor(ttl / 3600)}h ${Math.floor((ttl % 3600) / 60)}m`;
};

const fmtBytes = (n) => {
  if (!n) return "—";
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / 1024 / 1024).toFixed(2)} MB`;
};

export default function AdminCache() {
  const [stats, setStats] = useState(null);
  const [top, setTop] = useState([]);
  const [keys, setKeys] = useState([]);
  const [pattern, setPattern] = useState("*");
  const [busy, setBusy] = useState(false);
  const [peek, setPeek] = useState(null);
  const [auto, setAuto] = useState(false);

  const load = useCallback(async () => {
    setBusy(true);
    try {
      const [s, t, k] = await Promise.all([
        api.get("/admin/cache/stats"),
        api.get("/admin/cache/top?limit=10"),
        api.get(`/admin/cache/keys?pattern=${encodeURIComponent(pattern)}&limit=100`),
      ]);
      setStats(s.data);
      setTop(t.data?.items || []);
      setKeys(k.data?.items || []);
    } catch {
      toast.error("Failed to load cache stats.");
    } finally {
      setBusy(false);
    }
  }, [pattern]);

  useEffect(() => { document.title = "Cache — IndiaODR Admin"; load(); }, [load]);

  useEffect(() => {
    if (!auto) return;
    const id = setInterval(load, 5000);
    return () => clearInterval(id);
  }, [auto, load]);

  const flush = async () => {
    if (!window.confirm("Flush ALL cache keys? Public reads will hit Mongo until the cache repopulates.")) return;
    try {
      const { data } = await api.post("/admin/cache/flush");
      toast.success(`Flushed ${data.deleted} keys.`);
      load();
    } catch {
      toast.error("Flush failed.");
    }
  };

  const inspect = async (key) => {
    try {
      const { data } = await api.get(`/admin/cache/peek?key=${encodeURIComponent(key)}`);
      setPeek(data);
    } catch {
      toast.error("Could not load value.");
    }
  };

  const hitRate = stats ? Math.round((stats.hit_rate || 0) * 100) : 0;
  const hitColor = hitRate >= 80 ? "text-[#10B981]" : hitRate >= 50 ? "text-[var(--io-gold)]" : "text-[#F59E0B]";

  return (
    <AdminLayout>
      <div className="flex items-start justify-between mb-6 flex-wrap gap-3" data-testid="cache-page">
        <div>
          <div className="flex items-center gap-2 text-[11px] font-mono text-[var(--io-text-2)]">
            <Database size={13} className="text-[var(--io-gold)]"/> Redis cache observability
          </div>
          <h1 className="font-serif text-[28px] md:text-[36px] mt-2 text-[var(--io-text)]">Cache panel</h1>
          <div className="text-[12px] font-mono text-[var(--io-muted)] mt-1">
            {stats?.redis_url} · default TTL {stats?.default_ttl}s · {stats?.now ? new Date(stats.now).toLocaleTimeString() : ""}
          </div>
        </div>
        <div className="flex flex-wrap gap-2">
          <label className="btn btn-ghost !text-[12px] !py-2 cursor-pointer flex items-center gap-1.5">
            <input type="checkbox" checked={auto} onChange={(e) => setAuto(e.target.checked)} data-testid="cache-auto-refresh"/>
            Auto-refresh
          </label>
          <button onClick={load} disabled={busy} className="btn btn-ghost !text-[12px] !py-2" data-testid="cache-reload">
            {busy ? <Loader2 size={14} className="animate-spin"/> : <RefreshCw size={14}/>} Refresh
          </button>
          <button onClick={flush} className="btn !text-[12px] !py-2" style={{ background: "#EF4444", color: "#fff" }} data-testid="cache-flush">
            <Trash2 size={14}/> Flush all
          </button>
        </div>
      </div>

      {/* Stat tiles */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-6" data-testid="cache-stats-grid">
        <Stat label="Hit rate" value={`${hitRate}%`} accent={hitColor}/>
        <Stat label="Hits" value={stats?.hits ?? "—"} accent="text-[#10B981]"/>
        <Stat label="Misses" value={stats?.misses ?? "—"} accent="text-[#F59E0B]"/>
        <Stat label="Cached keys" value={stats?.payload_keys ?? "—"} accent="text-[var(--io-gold)]"/>
        <Stat label="Memory" value={stats?.memory_used ?? "—"}/>
      </div>

      <div className="grid lg:grid-cols-2 gap-5">
        {/* Top hot keys */}
        <div className="io-card-el p-5" data-testid="cache-top">
          <div className="flex items-center justify-between mb-3">
            <div className="eyebrow">Top hot keys (by hits)</div>
            <div className="text-[10px] font-mono text-[var(--io-muted)]">live</div>
          </div>
          {top.length === 0 ? (
            <div className="text-[12px] text-[var(--io-muted)]">No cache hits recorded yet — make some public requests.</div>
          ) : (
            <table className="w-full text-[12px] font-mono">
              <thead>
                <tr className="text-[10px] text-[var(--io-muted)] uppercase tracking-[0.15em] border-b border-[var(--io-border)]">
                  <th className="text-left py-2">Key</th>
                  <th className="text-right py-2">Hits</th>
                  <th className="text-right py-2">TTL</th>
                  <th className="text-right py-2"></th>
                </tr>
              </thead>
              <tbody>
                {top.map((row) => (
                  <tr key={row.full_key} className="border-b border-[var(--io-border)]/60 last:border-0">
                    <td className="py-2 truncate max-w-[240px] text-[var(--io-text)]">{row.key}</td>
                    <td className="py-2 text-right text-[var(--io-gold)]">{row.hits}</td>
                    <td className="py-2 text-right text-[var(--io-text-2)]">{fmtTtl(row.ttl)}</td>
                    <td className="py-2 text-right">
                      <button onClick={() => inspect(row.full_key)} className="text-[var(--io-text-2)] hover:text-[var(--io-gold)]" data-testid={`cache-peek-${row.key}`}><Eye size={12}/></button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        {/* All keys browser */}
        <div className="io-card-el p-5" data-testid="cache-keys">
          <div className="flex items-center justify-between mb-3 gap-2">
            <div className="eyebrow">Cached keys</div>
            <div className="flex items-center gap-1.5">
              <Search size={12} className="text-[var(--io-muted)]"/>
              <input
                value={pattern}
                onChange={(e) => setPattern(e.target.value)}
                onBlur={load}
                onKeyDown={(e) => e.key === "Enter" && load()}
                className="bg-[var(--io-bg-2)] border border-[var(--io-border)] focus:border-[var(--io-gold)] rounded px-2 py-1 text-[11px] font-mono text-[var(--io-text)] outline-none w-[120px]"
                placeholder="* / homepage*"
                data-testid="cache-pattern-input"
              />
            </div>
          </div>
          {keys.length === 0 ? (
            <div className="text-[12px] text-[var(--io-muted)]">No keys match the pattern.</div>
          ) : (
            <table className="w-full text-[12px] font-mono">
              <thead>
                <tr className="text-[10px] text-[var(--io-muted)] uppercase tracking-[0.15em] border-b border-[var(--io-border)]">
                  <th className="text-left py-2">Key</th>
                  <th className="text-right py-2">Size</th>
                  <th className="text-right py-2">TTL</th>
                  <th className="text-right py-2"></th>
                </tr>
              </thead>
              <tbody>
                {keys.map((row) => (
                  <tr key={row.full_key} className="border-b border-[var(--io-border)]/60 last:border-0">
                    <td className="py-2 truncate max-w-[240px] text-[var(--io-text)]">{row.key}</td>
                    <td className="py-2 text-right text-[var(--io-text-2)]">{fmtBytes(row.size_bytes)}</td>
                    <td className="py-2 text-right text-[var(--io-text-2)]">{fmtTtl(row.ttl)}</td>
                    <td className="py-2 text-right">
                      <button onClick={() => inspect(row.full_key)} className="text-[var(--io-text-2)] hover:text-[var(--io-gold)]"><Eye size={12}/></button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>

      {peek && (
        <div className="io-card-el p-5 mt-5" data-testid="cache-peek-panel">
          <div className="flex items-center justify-between mb-3">
            <div>
              <div className="eyebrow">Value preview</div>
              <div className="font-mono text-[12px] text-[var(--io-text)] mt-1 break-all">{peek.full_key || peek.key}</div>
              <div className="font-mono text-[11px] text-[var(--io-muted)] mt-0.5">
                {peek.found
                  ? `${fmtBytes(peek.size_bytes)} · TTL ${fmtTtl(peek.ttl)}${peek.truncated ? " · truncated" : ""}`
                  : "key not found"}
              </div>
            </div>
            <button onClick={() => setPeek(null)} className="text-[var(--io-muted)] hover:text-[var(--io-text)] text-[20px] leading-none" aria-label="Close">×</button>
          </div>
          {peek.found && (
            <pre className="bg-[var(--io-bg-2)] border border-[var(--io-border)] rounded-md p-3 text-[11px] font-mono text-[var(--io-text-2)] overflow-x-auto max-h-[300px]">
              {peek.json_preview ? JSON.stringify(peek.json_preview, null, 2) : peek.raw_preview}
            </pre>
          )}
        </div>
      )}
    </AdminLayout>
  );
}
